"use client"

import { createContext, useContext, useEffect, useState } from "react"
import { useAuth } from "./AuthContext"
import config from "../config"
import { WS_SERVER_ERROR, WS_SERVER_DISCORD_REQUIRED, WS_SERVER_USER_DATA, IN_RANGE_PLAYERS_UPDATE } from "../proto/wsServerCommandIds"
import { WS_CLIENT_AUTH, WS_CLIENT_SUBSCRIBE_IN_RANGE_PLAYERS, WS_CLIENT_UNSUBSCRIBE_IN_RANGE_PLAYERS } from "../proto/wsClientRequestIds"
import { TOAST_SEVERITY, useToast } from "./ToastContext"
import { useInRangePlayers } from "./InRangePlayersContext"

// Create a singleton WebSocket service outside of React's rendering cycle
class WebSocketService {
  constructor() {
    this.ws = null
    this.reconnectTimeout = null
    this.pingInterval = null
    this.listeners = {
      onStatusChange: [],
      onDiscordRequired: [],
      onError: [],
    }
    this.status = "disconnected" // 'connected', 'disconnected', 'connecting'
    this.discordRequired = false
    this.authActions = null
    this.inRangePlayersActions = null 
    this.toastFn = null
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 10
    this.baseReconnectDelay = 1000 // Start with 1 second
    this.shouldReconnect = true
    this.currentUser = null
  }

  setAuthActions(authActions) {
    this.authActions = authActions
  }

  setInRangePlayersActions(inRangePlayersActions) {
    this.inRangePlayersActions = inRangePlayersActions
  }

  setToastFn(toastFn) {
    this.toastFn = toastFn
  }

  getStatus() {
    return this.status
  }

  getDiscordRequired() {
    return this.discordRequired
  }

  addListener(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event].push(callback)
    }
    return () => this.removeListener(event, callback)
  }

  removeListener(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter((cb) => cb !== callback)
    }
  }

  notifyListeners(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach((callback) => callback(data))
    }
  }

  setStatus(status) {
    if (this.status !== status) {
      this.status = status
      this.notifyListeners("onStatusChange", status)
    }
  }

  setDiscordRequired(required) {
    if (this.discordRequired !== required) {
      this.discordRequired = required
      this.notifyListeners("onDiscordRequired", required)
    }
  }

  showToast(toast, severity, duration) {
    if (this.toastFn) {
      // Convert the toast object format to match our ToastContext
      this.toastFn(toast, severity ?? TOAST_SEVERITY.INFO, duration ?? 3000)
    }
  }

  // Calculate reconnect delay with exponential backoff
  getReconnectDelay() {
    // Exponential backoff: baseDelay * 2^attempts (with some randomness)
    const delay = this.baseReconnectDelay * Math.pow(1.5, this.reconnectAttempts)
    // Add jitter to prevent all clients from reconnecting simultaneously
    const jitter = Math.random() * 0.5 + 0.75 // 0.75-1.25 multiplier
    return Math.min(delay * jitter, 30000) // Cap at 30 seconds
  }

  // Schedule a reconnection attempt
  scheduleReconnect(user) {
    if (!this.shouldReconnect || this.reconnectAttempts >= this.maxReconnectAttempts) {
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        this.showToast(
          "Connection failed. Maximum reconnection attempts reached. Please refresh the page.",
          TOAST_SEVERITY.ERROR,
          5000,
        )
      }
      return
    }

    const delay = this.getReconnectDelay()
    this.reconnectAttempts++

    console.log(`Scheduling reconnect attempt ${this.reconnectAttempts} in ${Math.round(delay / 1000)}s`)

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
    }

    this.reconnectTimeout = setTimeout(() => {
      if (this.shouldReconnect) {
        this.connect(user)
      }
    }, delay)
  }

  // Reset reconnection state after successful connection
  resetReconnectState() {
    this.reconnectAttempts = 0
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }
  }

  connect(user) {
    // Store the current user for reconnection purposes
    this.currentUser = user
    this.shouldReconnect = true

    // If already connected or connecting, don't create a new connection
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) {
      return
    }

    // Clean up any existing connection
    this.cleanupConnection()

    const isProd = window.location.protocol === "https:";
    const protocol = isProd ? "wss:" : "ws:"
    const wsUrl = `${protocol}//${window.location.hostname}${isProd ? "" : ":8080"}`

    try {
      this.ws = new WebSocket(wsUrl)
      this.setStatus("connecting")

      this.ws.onopen = () => {
        this.setStatus("connected")
        console.log("WebSocket Connected")
        this.showToast("Connected to DarkEdge server", TOAST_SEVERITY.SUCCESS, 3000)

        // Reset reconnection state on successful connection
        this.resetReconnectState()

        // Send authentication
        if (user) {
          const token = localStorage.getItem("token")
          this.ws.send(JSON.stringify({ type: WS_CLIENT_AUTH, token }))
        }
      }

      this.ws.onmessage = (event) => {
        let data
        try {
          data = JSON.parse(event.data)
        } catch (e) {
          return
        }

        if (data.type === WS_SERVER_ERROR) {
          this.showToast(data.message, TOAST_SEVERITY.ERROR, 3000)
          this.notifyListeners("onError", data.message)
        } else if (data.type === WS_SERVER_DISCORD_REQUIRED) {
          this.setDiscordRequired(true)
          this.showToast("You are not in our Discord server. Please join to continue.", TOAST_SEVERITY.WARNING, 3000)
        } else if (data.type === WS_SERVER_USER_DATA) {
          // Update user data in AuthContext using the stored reference to authActions
          const { roles, username, avatar, global_name } = data.data
          const currentUser = JSON.parse(localStorage.getItem("user") || "{}")
          const updatedUser = {
            ...currentUser,
            roles,
            username,
            avatar,
            global_name,
          } 
          localStorage.setItem("user", JSON.stringify(updatedUser))

          // Use the stored reference to setUser to avoid re-rendering WebSocketProvider
          if (this.authActions && this.authActions.setUser) {
            this.authActions.setUser(updatedUser)
          }

          this.setDiscordRequired(false)
        } else if (data.type === IN_RANGE_PLAYERS_UPDATE) {
            if (this.inRangePlayersActions && this.inRangePlayersActions.setInRangePlayers) {
              this.inRangePlayersActions.setInRangePlayers(data.data)
            }
        }
      }

      this.ws.onclose = (event) => {
        this.setStatus("disconnected")
        console.log(`WebSocket Disconnected: ${event.code} - ${event.reason}`)

        this.showToast("Connection lost. Attempting to reconnect...", TOAST_SEVERITY.WARNING, 3000)

        // Schedule reconnection if we should reconnect
        if (this.shouldReconnect && this.currentUser) {
          this.scheduleReconnect(this.currentUser)
        }
      }

      this.ws.onerror = (error) => {
        console.error("WebSocket Error:", error)
        // The onclose handler will be called after this
      }

      // Keep alive with ping/pong
      this.pingInterval = setInterval(() => {
        if (this.ws?.readyState === WebSocket.OPEN) {
          try {
            this.ws.send("ping")
          } catch (e) {
            console.error("Error sending ping:", e)
            // Force close and reconnect if ping fails
            this.cleanupConnection()
            this.scheduleReconnect(this.currentUser)
          }
        }
      }, config.WEBSOCKET_PING_INTERVAL)
    } catch (error) {
      console.error("Error creating WebSocket:", error)
      this.setStatus("disconnected")
      this.scheduleReconnect(this.currentUser)
    }
  }

  // Clean up connection without scheduling reconnect
  cleanupConnection() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval)
      this.pingInterval = null
    }

    if (this.ws) {
      // Remove all event handlers to prevent them from firing
      this.ws.onopen = null
      this.ws.onmessage = null
      this.ws.onclose = null
      this.ws.onerror = null

      // Close the connection if it's still open
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        try {
          this.ws.close()
        } catch (e) {
          console.error("Error closing WebSocket:", e)
        }
      }
      this.ws = null
    }
  }

  disconnect() {
    // Indicate that we should not reconnect
    this.shouldReconnect = false
    this.currentUser = null

    // Clear reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }

    // Clean up the connection
    this.cleanupConnection()
    this.setStatus("disconnected")
  }

  subscribeToInRangePlayers() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({
          type: WS_CLIENT_SUBSCRIBE_IN_RANGE_PLAYERS
        }))
      } catch (error) {
        console.error("Error subscribing to in-range players:", error)
      }
    }
  }

  unsubscribeFromInRangePlayers() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({
          type: WS_CLIENT_UNSUBSCRIBE_IN_RANGE_PLAYERS
        }))
      } catch (error) {
        console.error("Error unsubscribing from in-range players:", error)
      }
    }
  }
}

// Create a singleton instance
const webSocketService = new WebSocketService()

// Create React context
const WebSocketContext = createContext()

export const useWebSocket = () => useContext(WebSocketContext)

export const WebSocketProvider = ({ children }) => {
  const { showToast } = useToast()
  const toast = showToast
  const { user, setUser } = useAuth()
  const { inRangePlayers, setInRangePlayers } = useInRangePlayers()
  const [wsStatus, setWsStatus] = useState(webSocketService.getStatus())
  const [discordRequired, setDiscordRequired] = useState(webSocketService.getDiscordRequired())

  // Set up the toast function once
  useEffect(() => {
    webSocketService.setToastFn(toast)

    return () => {
      webSocketService.setToastFn(null)
    }
  }, [toast])

  // Set up auth actions once
  useEffect(() => {
    webSocketService.setAuthActions({ setUser })

    return () => {
      webSocketService.setAuthActions(null)
    }
  }, [setUser])

  // Set up in range players actions once
  useEffect(() => {
    webSocketService.setInRangePlayersActions({ setInRangePlayers })

    return () => {
      webSocketService.setInRangePlayersActions(null)
    }
  }, [setInRangePlayers])

  // Set up listeners for status and discord required changes
  useEffect(() => {
    const statusUnsubscribe = webSocketService.addListener("onStatusChange", setWsStatus)
    const discordUnsubscribe = webSocketService.addListener("onDiscordRequired", setDiscordRequired)

    return () => {
      statusUnsubscribe()
      discordUnsubscribe()
    }
  }, [])

  // Connect or disconnect based on user state
  useEffect(() => {
    if (!user) {
      webSocketService.disconnect()
      return
    }

    webSocketService.connect(user)

    // Add window event listeners to handle page visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && user) {
        // If the page becomes visible and the connection is closed, reconnect
        if (
          !webSocketService.ws ||
          webSocketService.ws.readyState === WebSocket.CLOSED ||
          webSocketService.ws.readyState === WebSocket.CLOSING
        ) {
          console.log("Page became visible, reconnecting WebSocket")
          webSocketService.connect(user)
        }
      }
    }

    const handleOnline = () => {
      if (user) {
        console.log("Browser went online, reconnecting WebSocket")
        webSocketService.connect(user)
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    window.addEventListener("online", handleOnline)

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      window.addEventListener("online", handleOnline)
      // No need to disconnect on component unmount
      // The service will stay alive
    }
  }, [user])

  // Provide a manual reconnect function
  const reconnect = () => {
    if (user) {
      webSocketService.disconnect() // Clean up first
      setTimeout(() => webSocketService.connect(user), 500) // Reconnect after a short delay
    }
  }

  return (
    <WebSocketContext.Provider
      value={{
        ws: webSocketService.ws,
        discordRequired,
        wsStatus,
        connect: () => webSocketService.connect(user),
        disconnect: () => webSocketService.disconnect(),
        reconnect,
        subscribeToInRangePlayers: () => webSocketService.subscribeToInRangePlayers(),
        unsubscribeFromInRangePlayers: () => webSocketService.unsubscribeFromInRangePlayers(),
      }}
    >
      {children}
    </WebSocketContext.Provider>
  )
}
